import { BookModel } from '../interfaces/book.interface';

/**
 * Print-aligned completeness of a book — the client mirror of
 * server/src/helpers/bookCompleteness.helper.ts. Keep the two in sync.
 *
 * Derived from the pages the book ACTUALLY has, NOT from `pageTotal` (which counts
 * 3 extra non-illustration spreads — cover/branding/back-cover). A book is
 * "shippable" when every contiguous page `page1..pageN` (N = highest present
 * index) has a usable `imageURL` and isn't errored, there's no gap in that
 * sequence, the book isn't still generating, and a cover (`coverURL ||
 * coverURLDesign`) is present. Deliberately step-agnostic.
 */
export interface BookCompleteness {
  isComplete: boolean;
  expectedPages: number;
  missingPageCount: number;
  missingImageCount: number;
  errorPageCount: number;
  hasCover: boolean;
  isProcessing: boolean;
  /** Human-readable, severity-ordered. `reasons[0]` is the badge text. */
  reasons: string[];
}

type BookCompletenessInput = Pick<
  BookModel,
  'pages' | 'coverURL' | 'coverURLDesign' | 'isImageProcessing'
>;

export function computeBookCompleteness(
  book: BookCompletenessInput | null | undefined,
): BookCompleteness {
  const pages = book?.pages ?? {};
  const isProcessing = book?.isImageProcessing === true;
  const hasCover = !!(book?.coverURL || book?.coverURLDesign);

  // Highest illustration page index actually present → contiguous expected range
  // (page1..maxIndex). Derived from real pages, never from pageTotal.
  const indices = Object.keys(pages)
    .map((k) => /^page(\d+)$/.exec(k))
    .filter((m): m is RegExpExecArray => m !== null)
    .map((m) => Number(m[1]))
    .filter((n) => n > 0);
  const expectedPages = indices.length ? Math.max(...indices) : 0;

  let missingPageCount = 0;
  let missingImageCount = 0;
  let errorPageCount = 0;

  for (let i = 1; i <= expectedPages; i++) {
    const page = pages[`page${i}`];
    if (!page) {
      missingPageCount++;
      continue;
    }
    // A usable `imageURL` is all the print pipeline needs — it renders the image
    // whenever imageURL is present and ignores `error`. Check the image first so a
    // page that prints fine isn't flagged by a stale `error: true` left behind by a
    // failed regeneration; `error` only explains why a *missing* image is missing.
    if (page.imageURL) {
      continue;
    }
    if (page.error === true) {
      errorPageCount++;
      continue;
    }
    missingImageCount++;
  }

  const missingImages = missingPageCount + missingImageCount + errorPageCount;

  const reasons: string[] = [];
  if (expectedPages <= 0) {
    reasons.push('Book has no pages yet');
  } else if (missingImages > 0) {
    // `isImageProcessing` only refines the message (in-flight vs stuck/failed); it
    // never independently blocks. A stuck-true flag on a fully-imaged book is a known
    // stale state, and the pages are the source of truth (an in-progress book has
    // pages without an imageURL, already counted above).
    reasons.push(
      isProcessing ? 'Still generating' : `Missing ${missingImages} image${missingImages === 1 ? '' : 's'}`,
    );
  }
  if (!hasCover) reasons.push('Missing cover');

  const isComplete = expectedPages > 0 && hasCover && missingImages === 0;

  return {
    isComplete,
    expectedPages,
    missingPageCount,
    missingImageCount,
    errorPageCount,
    hasCover,
    isProcessing,
    reasons,
  };
}

/** Short badge label for an incomplete book, or null when complete. */
export function getIncompleteBadge(c: BookCompleteness): string | null {
  return c.isComplete ? null : c.reasons[0] ?? 'Incomplete';
}
